<template>
    <div class="relative vue-select" :class="{'hide-checkboxes': hideCheckbox, 'error': error}">
        <multiselect
            :modelValue="optionsSelected"
            @update:modelValue="onInput"
            :options="_options"
            :options-limit="optionsLimit"
            :multiple="multiple"
            :allow-empty="allowEmpty"
            :close-on-select="closeOnSelect"
            :clear-on-select="false"
            :preserve-search="true"
            :placeholder="placeholderLabel"
            :label="labelField"
            :track-by="trackByField"
            :disabled="disable"
            @open="isOpened = true"
            @close="onClose"
            @select="onSelect"
            @remove="onRemove"
            :group-values="hasGroupedOptions ? groupValues : null"
            :group-label="hasGroupedOptions ? groupLabel : null"
            :group-select="hasGroupedOptions ? groupSelect : null"
        >
            <template #selection="{ values, isOpen }">
                <span class="multiselect__single" v-if="values.length && !isOpen">
                    {{ selectedCount }} {{ transChoice('global.selected_options', selectedCount) }}
                </span>
            </template>

            <template #option="{ option }">
                <span v-if="option.$isLabel">{{ option.$groupLabel }}</span>
                <span v-else>{{ option[labelField] }}</span>
            </template>

            <template #caret>
                <i :class="{ 'transform rotate-180': isOpened }" class="absolute top-0 right-0 zmdi zmdi-chevron-down dropdown-icon"></i>
            </template>

            <template #noResult>
                {{ trans('app.no_results') }}
            </template>

            <template #noOptions>
                {{ trans('app.no_results') }}
            </template>
        </multiselect>

        <a class="clear-filters" v-show="selectedCount > 0" @click.prevent="onClearAll" href="#">
            <i class="zmdi zmdi-close"></i>
        </a>
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect';

export default {
    components: {
        Multiselect
    },

    props: {
        options: {
            type: Array,
            default: () => []
        },
        selectAllOption: {
            type: Boolean,
            default: false
        },
        multiple: {
            type: Boolean,
            default: true
        },
        allowEmpty: {
            type: Boolean,
            default: true
        },
        closeOnSelect: {
            type: Boolean,
            default: false
        },
        optionsLimit: {
            type: Number,
            default: 1000
        },
        placeholder: {
            type: String,
            default: trans('global.multiselect.select_options')
        },
        value: {
            type: [Array, Object],
            default: () => this.multiple ? [] : {}
        },
        optionsToDeselect: {
            type: Object,
            default: () => {
                return {}
            }
        },
        labelField: {
            // this is what <LABEL TEXT> shows up in the dropdown list
            type: String,
            default: 'text',
        },
        trackByField: {
            // this is what multiselect uses to track the value
            type: String,
            default: 'id',
        },
        hideCheckbox: {
            type: Boolean,
            default: false
        },
        groupValues: {
            type: String,
            default: 'options'
        },
        groupLabel: {
            type: String,
            default: 'label'
        },
        hasGroupedOptions: {
            type: Boolean,
            default: false
        },
        groupSelect: {
            type: Boolean,
            default: false
        },
        confirmOnClearAll: {
            type: Boolean,
            default: false
        },
        confirmOnClearAllMessage: {
            type: String,
            default: trans('global.clear_all_selections_default_message')
        },
        disable: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            isOpened: false,
            currentSelectedOptionId: null,
        }
    },

    mounted () {
        this.setInitialOptions();
    },

    computed: {
        selectedCount () {
            if (!this.multiple) {
                return Object.keys(this.value).length > 0 ? 1 : 0;
            }

            return this.value.filter(item => item[this.trackByField] !== '0').length
        },

        placeholderLabel() {
            if (!this.multiple) {
                return Object.keys(this.value).length > 0 ? this.value[this.labelField] : this.placeholder;
            }

            return this.selectedCount
                ? `${this.selectedCount} ${transChoice('global.selected_options', this.selectedCount)}`
                : this.placeholder;
        },

        _options () {
            const options = [...this.options];
            if (this.selectAllOption && this.options.length > 1) {
                options.unshift(this.selectAllObj);
            }
            return options;
        },

        optionsSelected() {
            const allOptionsSelected = this.value.length === this.options.length;

            if (this.selectAllOption && allOptionsSelected) {
                return [this.selectAllObj, ...this.value];
            }

            return this.value;
        },

        selectAllObj() {
            return { [this.trackByField]: "0", [this.labelField]: this.trans('global.select_all') };
        }
    },

    methods: {
        onSelect (option) {
            if (this.selectAllOption && option[this.trackByField] === '0') {
                this.$nextTick(() => {
                    this.$emit('input', this.options);
                });
            }

            this.currentSelectedOptionId = option[this.trackByField];
        },

        onRemove (option) {
            if (this.selectAllOption && option[this.trackByField] === '0') {
                this.$nextTick(() => {
                    this.$emit('input', []);
                });
            }
        },

        setInitialOptions () {
            if (!this.multiple) {
                this.$emit('input', this.value);
                return;
            }

            if (this.value.length > 0) {
                const selectedOptions = this._options.filter(option => this.value.includes(option[this.trackByField]));
                this.$emit('input', selectedOptions);
            }
        },

        deselectOptions(selectedOption) {
            if(! this.optionsToDeselect) return;

            if (!this.optionsToDeselect[selectedOption]) return;

            this.$nextTick(() => {
                const filteredOptions = this.value.filter(option => !this.optionsToDeselect[selectedOption].includes(option[this.trackByField]))
                this.$emit('input', filteredOptions);
            });
        },

        onInput (value) {
            if (!this.multiple) {
                this.$emit('input', value);
                return;
            }

            const newValue = value.filter(item => item[this.trackByField] !== '0');
            if (this.selectAllOption && newValue.length === this.options.length) {
                this.$emit('input', this.options);
            } else {
                this.$emit('input', newValue);
            }
            if (Object.keys(this.optionsToDeselect).length > 0) {
                this.$nextTick(() => {
                    this.deselectOptions(this.currentSelectedOptionId);
                });
            }
        },

        onClose() {
            this.isOpened = false

            this.$emit('close');
        },

        onClearAll() {
            if (this.confirmOnClearAll) {
                swal({
                    title: trans('global.are_you_sure'),
                    text: this.confirmOnClearAllMessage,
                    showCancelButton: true,
                    cancelButtonText: trans('global.cancel'),
                }, (confirmClearAll) => {
                    if(confirmClearAll) {
                        this.emitOnClearAll();
                    }
                });
            } else {
                this.emitOnClearAll();
            }
        },

        emitOnClearAll() {
            this.$emit('input', []);
            this.$emit('clear-all');
        }
    },
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style lang="scss">
@import 'Sass/system/global/colors';

.vue-select.error {
    .multiselect {
        .multiselect__tags {
            border-color: $danger-color;
        }
    }
}
</style>